/* prettier-ignore-file */
/**
 * Edit Booking Wizard — staff-only, sectioned replacement for EditBooking.js.
 *
 * Architecture (why this page is simple where EditBooking.js is not):
 *
 *   FROZEN ORIGINAL + DRAFT + WHOLESALE RE-DERIVE. The booking document is read
 *   once and never mutated in memory. Edits go into a draft shaped exactly like
 *   the booking wizard's, and EVERY derived number — pricing, discounts,
 *   availability, the amount to charge or refund — is recomputed from scratch on
 *   each change by the same engine that priced the booking at creation
 *   (utils/rentalPricing.js via utils/editBookingWizard.js). There is no
 *   incremental patching, so there is no stale intermediate state.
 *
 * Flow: hub ("what would you like to change?") -> one section at a time
 * (reusing the booking wizard's Step components) -> apply to draft -> the whole
 * draft revalidates -> review (old vs new + exact money movement) -> one commit.
 *
 * Commit order: concurrency guard -> availability backstop -> move money
 * (Stripe/cash) -> ONE updateDoc with the fully re-derived document -> standard
 * edited-booking email + SMS (sendEditedBookingNotification, non-blocking).
 *
 * Money rules: card payments refund to the card (processRefund -> Stripe); cash
 * may be refunded in cash. Additional charges go to the saved card, the till, or
 * a Stripe payment link (webhook records that payment when it is paid).
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Modal,
  ActivityIndicator,
} from "react-native";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, updateDoc, arrayUnion, Timestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, app } from "../firebase.config";
import AuthHeader from "../components/AuthHeader";
import StepDates from "../components/wizard/StepDates";
import StepProducts from "../components/wizard/StepProducts";
import StepTransportation from "../components/wizard/StepTransportation";
import StepCustomerInfo, { isValidEmail, isValidPhone } from "../components/wizard/StepCustomerInfo";
import WizardSummary from "../components/wizard/WizardSummary";
import { useConfirmDialog } from "../components/ConfirmDialog";
import {
  useBookings,
  usePendingBookings,
  useProducts,
  useSettings,
  useAuthStore,
  useStripeTestMode,
} from "../stores";
import { getCollectionPath } from "../utils/dbPaths";
import { computeAvailabilityForRange } from "../utils/rentalAvailability";
import { readPricingSettings, getRentalDays, parseRentalDate, MINIMUM_ORDER_CENTS } from "../utils/rentalPricing";
import { formatCentsAsUSD, capitalizeWords } from "../utils/formatting";
import { createChangeTrackingEntry } from "../utils/helperFuns";
import {
  bookingToDraft,
  buildEffectiveCatalogs,
  deriveEditQuote,
  summarizeDraftChanges,
  computeDateChangeFeeCents,
  computeSettlement,
  buildPaymentProfile,
  allocateRefund,
  buildNotificationChanges,
  buildQuantityChanges,
  buildCardChargeEntry,
  buildCashChargeEntry,
  buildCardRefundEntry,
  buildCashRefundEntry,
  buildUpdatedBookingFields,
  findDraftShortages,
} from "../utils/editBookingWizard";
import { colors, elevation } from "../theme";

// Explainer for the resend button. Shown on hover where a real pointer exists;
// on touch screens it is shown by long-press instead, because iOS fires
// synthetic mouse events on tap that would leave a hover tip stuck open.
const RESEND_TIP =
  "Re-sends the booking info email, PIN included, to the customer's email on file.";
const CAN_HOVER =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(hover: hover)").matches;

const functions = getFunctions(app);

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const fmtDay = (value) => {
  const date = parseRentalDate(value);
  return date
    ? date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "—";
};

const fmtRange = (start, end) => {
  const days = getRentalDays(start, end);
  return `${fmtDay(start)} – ${fmtDay(end)}${days ? ` · ${days} day${days === 1 ? "" : "s"}` : ""}`;
};

/** Strip Dates/Timestamps so the payload survives httpsCallable serialization. */
const sanitizeForCallable = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof value === "number" || typeof value === "string" || typeof value === "boolean") return value;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value.toISOString();
  if (typeof value.toDate === "function") {
    const date = value.toDate();
    return date instanceof Date && !isNaN(date.getTime()) ? date.toISOString() : null;
  }
  if (Array.isArray(value)) return value.map(sanitizeForCallable);
  if (typeof value === "object") {
    const out = {};
    Object.keys(value).forEach((key) => { out[key] = sanitizeForCallable(value[key]); });
    return out;
  }
  return value;
};

const matchesBookingId = (booking, bookingId) =>
  booking && (booking.id === bookingId || booking.bookingId === bookingId);

/** Draft keyed against the effective catalog so renamed products still resolve. */
const makeDraft = (bookingData, products1, products2) => {
  const effectiveCatalogs = buildEffectiveCatalogs({ booking: bookingData, products1, products2 });
  return bookingToDraft(bookingData, effectiveCatalogs.products1);
};

// ---------------------------------------------------------------------------
// Booking picker — shown when the wizard is opened from the menu with no id
// ---------------------------------------------------------------------------

const BookingPicker = ({ bookings, onPick }) => {
  const [search, setSearch] = useState("");

  const rows = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const active = (bookings || []).filter(
      (booking) => booking && booking.cancelled !== true && booking.status !== "cancelled"
    );

    const term = search.trim().toLowerCase();
    const filtered = term
      ? active.filter((booking) => {
          const haystack = [
            booking.firstName,
            booking.lastName,
            `${booking.firstName || ""} ${booking.lastName || ""}`,
            booking.customerEmail,
            booking.customerPhone,
            booking.customerBookingID,
            booking.bookingId,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(term);
        })
      : active;

    const endMillis = (booking) => parseRentalDate(booking.endDate)?.getTime() ?? 0;
    const startMillis = (booking) => parseRentalDate(booking.startDate)?.getTime() ?? 0;
    const upcoming = filtered
      .filter((booking) => endMillis(booking) >= today.getTime())
      .sort((a, b) => startMillis(a) - startMillis(b));
    const past = filtered
      .filter((booking) => endMillis(booking) < today.getTime())
      .sort((a, b) => startMillis(b) - startMillis(a));

    return { upcoming, past: past.slice(0, 25) };
  }, [bookings, search]);

  const renderRow = (booking) => {
    const name = capitalizeWords(`${booking.firstName || ""} ${booking.lastName || ""}`.trim()) || "Unknown";
    const isCash = booking.paymentMethod === "cash" ||
      (booking.payments || []).some((payment) => payment?.method === "cash");
    return (
      <TouchableOpacity
        key={booking.bookingId || booking.id}
        dataSet={{ hover: "lift" }}
        style={styles.pickerRow}
        onPress={() => onPick(booking.bookingId || booking.id)}
      >
        <View style={{ flex: 1 }}>
          <Text style={styles.pickerName}>{name}</Text>
          <Text style={styles.pickerMeta}>
            #{booking.customerBookingID || "—"} · {fmtRange(booking.startDate, booking.endDate)}
          </Text>
        </View>
        <View style={styles.pickerRight}>
          <Text style={styles.pickerTotal}>{formatCentsAsUSD(booking.totalAmount || 0)}</Text>
          <Text style={styles.pickerMethod}>{isCash ? "cash" : "card"}</Text>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <ScrollView style={styles.content}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Which booking would you like to edit?</Text>
        <TextInput
          style={styles.searchInput}
          value={search}
          onChangeText={setSearch}
          placeholder="Search by name, email, phone or booking #"
          placeholderTextColor={colors.ink300}
          autoCapitalize="none"
        />
      </View>

      <View style={styles.section}>
        <Text style={styles.groupHeading}>Upcoming & active</Text>
        {rows.upcoming.length === 0 ? (
          <Text style={styles.emptyText}>Nothing matches.</Text>
        ) : (
          rows.upcoming.map(renderRow)
        )}
      </View>

      {rows.past.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.groupHeading}>Past</Text>
          {rows.past.map(renderRow)}
        </View>
      )}
      <View style={styles.bottomSpacer} />
    </ScrollView>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const EditBookingWizard = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const authChecked = useAuthStore((state) => state.authChecked);
  const bookings = useBookings((state) => state.bookings);
  const pendingBookings = usePendingBookings((state) => state.pendingBookings);
  const products1 = useProducts((state) => state.products1) || [];
  const products2 = useProducts((state) => state.products2) || [];
  const settings = useSettings((state) => state.settings);
  const useTestStripe = useStripeTestMode((state) => state.useTestStripe);

  const cfg = readPricingSettings(settings);

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [original, setOriginal] = useState(null); // frozen booking snapshot
  const [draft, setDraft] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [screen, setScreen] = useState("hub"); // hub | dates | products | transport | customer | review
  const [sectionSnapshot, setSectionSnapshot] = useState(null); // draft before entering a section
  const [submitting, setSubmitting] = useState(false);
  const [holdTick, setHoldTick] = useState(0);
  const [resendState, setResendState] = useState("idle"); // idle | sending | sent
  const [showResendTip, setShowResendTip] = useState(false);
  const resendTipTimer = useRef(null);
  useEffect(() => () => clearTimeout(resendTipTimer.current), []);
  const showResendTipBriefly = () => {
    clearTimeout(resendTipTimer.current);
    setShowResendTip(true);
    resendTipTimer.current = setTimeout(() => setShowResendTip(false), 3500);
  };

  // Review-screen choices
  const [chargeMethod, setChargeMethod] = useState(null); // saved_card | cash | link
  const [refundMode, setRefundMode] = useState("auto"); // auto | percent | custom
  const [refundPercentText, setRefundPercentText] = useState("100");
  const [refundCustomText, setRefundCustomText] = useState("");
  // Staff override of what the customer still owes (charge edits and unpaid
  // repricing). Refunds already have their own override controls below.
  const [owedOverrideOn, setOwedOverrideOn] = useState(false);
  const [owedOverrideText, setOwedOverrideText] = useState("");
  const [preferCashRefund, setPreferCashRefund] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Holds expire by age; re-derive availability once a minute (BookingWizard.js:119).
  useEffect(() => {
    const id = setInterval(() => setHoldTick((t) => t + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Staff only.
  useEffect(() => {
    if (authChecked && !isAuthenticated) navigate("/login");
  }, [authChecked, isAuthenticated, navigate]);

  // Reset when switching bookings.
  useEffect(() => {
    setOriginal(null);
    setDraft(null);
    setScreen("hub");
    setLoadFailed(false);
    setChargeMethod(null);
    setRefundMode("auto");
    setRefundCustomText("");
    setPreferCashRefund(false);
  }, [bookingId]);

  // Load the booking ONCE into a frozen snapshot: from the live store when it has
  // it, falling back to a direct read. Later store updates deliberately do NOT
  // replace the snapshot — the commit-time guard handles concurrent edits.
  useEffect(() => {
    if (!bookingId || original) return;
    const fromStore = (bookings || []).find((booking) => matchesBookingId(booking, bookingId));
    if (fromStore) {
      setOriginal(fromStore);
      setDraft(makeDraft(fromStore, products1, products2));
      return;
    }
    let cancelled = false;
    const load = async () => {
      for (const path of [getCollectionPath("bookings"), "bookings"]) {
        try {
          const snap = await getDoc(doc(db, path, bookingId));
          if (snap.exists()) {
            if (!cancelled) {
              const data = snap.data();
              setOriginal(data);
              setDraft(makeDraft(data, products1, products2));
            }
            return;
          }
        } catch (error) {
          console.error(`⚠️ Could not read ${path}/${bookingId}:`, error);
        }
      }
      if (!cancelled) setLoadFailed(true);
    };
    load();
    return () => { cancelled = true; };
  }, [bookingId, bookings, original]);

  // A confirmed address with no distance behind it would price transportation at
  // the loading fee alone (BookingWizard.js:221) — force a re-confirm.
  useEffect(() => {
    if (!draft) return;
    const needsAddress = draft.deliveryNeeded || draft.homePickupNeeded;
    if (needsAddress && draft.addressConfirmed && draft.miles == null) {
      setDraft((current) => ({ ...current, addressConfirmed: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.addressConfirmed, draft?.miles, draft?.deliveryNeeded, draft?.homePickupNeeded]);

  // --- Derived state ---------------------------------------------------------

  const effective = useMemo(
    () =>
      original
        ? buildEffectiveCatalogs({ booking: original, products1, products2 })
        : { products1: [], products2: [] },
    [original, products1, products2]
  );

  // The edited booking must not compete with itself for inventory.
  const otherBookings = useMemo(
    () => (bookings || []).filter((booking) => !matchesBookingId(booking, bookingId)),
    [bookings, bookingId]
  );

  const availability = useMemo(
    () =>
      draft
        ? // otherBookings already excludes this booking; excludeBookingId also
          // covers any pending-bookings copy of it.
          findAvailability({
            draft,
            otherBookings,
            pendingBookings,
            effective,
            bookingId,
          })
        : { product1: {}, product2: {}, days: 0 },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft?.startDate, draft?.endDate, otherBookings, pendingBookings, effective, holdTick]
  );

  const { lineItems, quote } = useMemo(
    () =>
      draft
        ? deriveEditQuote({
            draft,
            effectiveProducts1: effective.products1,
            effectiveProducts2: effective.products2,
            settings,
          })
        : { lineItems: { product1: [], product2: [], totalBikes: 0 }, quote: { finalTotalCents: 0, days: 0 } },
    [draft, effective, settings]
  );

  const changes = useMemo(
    () =>
      original && draft
        ? summarizeDraftChanges({ booking: original, draft, lineItems })
        : null,
    [original, draft, lineItems]
  );

  const dateFee = useMemo(
    () =>
      original && draft && changes?.datesChanged
        ? computeDateChangeFeeCents({ booking: original, draft, settings })
        : { feeCents: 0, daysLost: 0 },
    [original, draft, changes?.datesChanged, settings]
  );

  const profile = useMemo(
    () => (original ? buildPaymentProfile(original) : null),
    [original]
  );

  const refundProcessingFeePercent = Number(settings?.refundProcessingFee?.percent ?? 3) || 0;

  const refundOverride = useMemo(() => {
    if (refundMode === "percent") {
      const percent = parseFloat(refundPercentText);
      return Number.isFinite(percent) ? { mode: "percent", percent } : null;
    }
    if (refundMode === "custom") {
      const dollars = parseFloat(refundCustomText.replace(/[^0-9.]/g, ""));
      return Number.isFinite(dollars)
        ? { mode: "custom", customCents: Math.round(dollars * 100) }
        : null;
    }
    return null;
  }, [refundMode, refundPercentText, refundCustomText]);

  const owedOverride = useMemo(() => {
    if (!owedOverrideOn) return null;
    const dollars = parseFloat((owedOverrideText || "").replace(/[^0-9.]/g, ""));
    return Number.isFinite(dollars) ? { customCents: Math.round(dollars * 100) } : null;
  }, [owedOverrideOn, owedOverrideText]);

  const settlement = useMemo(
    () =>
      original && draft && changes && profile
        ? computeSettlement({
            booking: original,
            quote,
            priceAffecting: changes.priceAffecting,
            dateChangeFeeCents: dateFee.feeCents,
            refundProcessingFeePercent,
            refundOverride,
            owedOverride,
            collectedCents:
              profile.cardPaidCents + profile.cashPaidCents -
              profile.cardRefundedCents - profile.cashRefundedCents,
          })
        : null,
    [original, draft, changes, profile, quote, dateFee.feeCents, refundProcessingFeePercent, refundOverride, owedOverride]
  );

  // What would be owed with no override -- the ceiling the override is clamped
  // and validated against. Named differently by each settlement branch.
  const owedBaseCents = settlement?.baseChargeCents ?? settlement?.baseOwedCents ?? 0;

  const allocation = useMemo(
    () =>
      settlement?.kind === "refund" && profile
        ? allocateRefund({
            profile,
            refundCents: settlement.refundCents,
            preferCash: preferCashRefund,
          })
        : null,
    [settlement, profile, preferCashRefund]
  );

  const shortages = useMemo(
    () =>
      original && draft
        ? findDraftShortages({
            draft,
            lineItems,
            bookings: otherBookings,
            pendingBookings,
            effectiveProducts1: effective.products1,
            effectiveProducts2: effective.products2,
            excludeBookingId: bookingId,
          })
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [original, draft, lineItems, otherBookings, pendingBookings, effective, holdTick]
  );

  // Everything that must be fixed before the edit can be saved.
  const blockers = useMemo(() => {
    if (!draft || !changes) return [];
    const list = [];
    const startDate = draft.startDate ? new Date(draft.startDate) : null;
    const endDate = draft.endDate ? new Date(draft.endDate) : null;
    if (!startDate || !endDate || startDate.getTime() === endDate.getTime()) {
      list.push("Pick a start and end date on different days");
    }
    if (lineItems.product1.length === 0 && lineItems.product2.length === 0) {
      list.push("The booking needs at least one item");
    }
    const needsAddress = draft.deliveryNeeded || draft.homePickupNeeded;
    if (needsAddress && (!draft.addressInput.trim() || !draft.addressConfirmed || draft.miles == null)) {
      list.push("Confirm the delivery address and distance");
    }
    if (!draft.firstName.trim()) list.push("First name is required");
    if (!draft.lastName.trim()) list.push("Last name is required");
    if (!isValidEmail(draft.customerEmail)) list.push("A valid email address is required");
    if (!isValidPhone(draft.customerPhone)) list.push("A 10-digit phone number is required");
    shortages.forEach((shortage) => {
      list.push(`${shortage.name}: need ${shortage.wanted}, only ${shortage.available} available for these dates`);
    });
    if (changes.priceAffecting && quote.finalTotalCents <= MINIMUM_ORDER_CENTS) {
      list.push(`The new total must be above ${formatCentsAsUSD(MINIMUM_ORDER_CENTS)}`);
    }
    if (settlement?.kind === "refund" && allocation && allocation.shortfallCents > 0) {
      list.push(
        `Refund exceeds what was collected — at most ${formatCentsAsUSD(
          profile.cardAvailableCents + profile.cashAvailableCents
        )} can be refunded (use a custom amount)`
      );
    }
    // An out-of-range override is refused rather than quietly clamped: silently
    // charging a different number than the one on screen is the worst outcome
    // here. computeSettlement still clamps as a backstop.
    if (owedOverrideOn) {
      const baseOwedCents = settlement?.baseChargeCents ?? settlement?.baseOwedCents ?? 0;
      const dollars = parseFloat((owedOverrideText || "").replace(/[^0-9.]/g, ""));
      if (!Number.isFinite(dollars)) {
        list.push("Enter the overridden amount owed");
      } else if (Math.round(dollars * 100) > baseOwedCents) {
        list.push(`The amount owed cannot be overridden above ${formatCentsAsUSD(baseOwedCents)}`);
      }
    }
    return list;
  }, [draft, changes, lineItems, shortages, quote, settlement, allocation, profile, owedOverrideOn, owedOverrideText]);

  // Charge method default, once a charge exists.
  useEffect(() => {
    if (settlement?.kind === "charge" && !chargeMethod && profile) {
      setChargeMethod(
        profile.savedCard ? "saved_card" : profile.cashPaidCents > 0 ? "cash" : "link"
      );
    }
  }, [settlement?.kind, chargeMethod, profile]);

  // Live warning if the booking changed underneath us (the hard guard runs at commit).
  const changedElsewhere = useMemo(() => {
    if (!original) return false;
    const live = (bookings || []).find((booking) => matchesBookingId(booking, bookingId));
    if (!live) return false;
    const liveMs = parseRentalDate(live.updatedAt)?.getTime() ?? null;
    const snapMs = parseRentalDate(original.updatedAt)?.getTime() ?? null;
    return liveMs !== snapMs;
  }, [bookings, bookingId, original]);

  // --- Draft update handlers -------------------------------------------------

  const updateDraft = (updates) => setDraft((current) => ({ ...current, ...updates }));

  const startDate = draft?.startDate ? new Date(draft.startDate) : null;
  const endDate = draft?.endDate ? new Date(draft.endDate) : null;
  const currentMonth = draft?.currentMonth ? new Date(draft.currentMonth) : new Date();

  // Range selection, carried over from BookingWizard.js:356.
  const handleSelectDate = (date) => {
    if (!startDate) {
      updateDraft({ startDate: date.toISOString(), endDate: null });
      return;
    }
    if (endDate) {
      if (date.getTime() === startDate.getTime() || date.getTime() === endDate.getTime()) {
        updateDraft({ startDate: null, endDate: null });
        return;
      }
      if (date < startDate) { updateDraft({ startDate: date.toISOString() }); return; }
      if (date > endDate) { updateDraft({ endDate: date.toISOString() }); return; }
      const distToStart = date - startDate;
      const distToEnd = endDate - date;
      if (distToStart <= distToEnd) updateDraft({ startDate: date.toISOString() });
      else updateDraft({ endDate: date.toISOString() });
      return;
    }
    if (date < startDate) {
      updateDraft({ startDate: date.toISOString(), endDate: startDate.toISOString() });
      return;
    }
    if (date.getTime() === startDate.getTime()) {
      updateDraft({ startDate: null, endDate: null });
      return;
    }
    updateDraft({ endDate: date.toISOString() });
  };

  const handleBikeChange = (product, nextQuantity) => {
    const info = availability.product1?.[product.id];
    const cap = info?.hasInventory ? info.available : Infinity;
    const quantity = Math.max(0, Math.min(nextQuantity, cap));
    const selectedBikes = { ...draft.selectedBikes };
    if (quantity === 0) delete selectedBikes[product.name];
    else selectedBikes[product.name] = quantity;
    updateDraft({ selectedBikes });
  };

  const handleAddOnChange = (product, nextQuantity) => {
    const option = product.option || "per-booking";
    const info = availability.product2?.[product.id];
    const cap = info?.hasInventory ? info.available : Infinity;
    const quantity = Math.max(0, Math.min(nextQuantity, cap));
    const selectedAddOns = { ...draft.selectedAddOns };
    if (quantity === 0) delete selectedAddOns[product.id];
    else if (option === "per-bike") selectedAddOns[product.id] = true;
    else selectedAddOns[product.id] = { quantity };
    updateDraft({ selectedAddOns });
  };

  const openSection = (key) => {
    setSectionSnapshot(draft); // for Cancel
    setScreen(key);
  };

  const cancelSection = () => {
    if (sectionSnapshot) setDraft(sectionSnapshot);
    setSectionSnapshot(null);
    setScreen("hub");
  };

  const applySection = () => {
    setSectionSnapshot(null);
    setScreen("hub");
  };

  const discardAll = async () => {
    if (
      !changes?.anyChange ||
      (await confirm("Discard all changes?", ["The original booking will be reloaded."], {
        okLabel: "Discard changes",
        cancelLabel: "Keep editing",
        danger: true,
      }))
    ) {
      setDraft(makeDraft(original, products1, products2));
      setScreen("hub");
    }
  };

  // --- Commit ----------------------------------------------------------------

  const { dialog, ask, confirm, notice } = useConfirmDialog();

  const handleCommit = async () => {
    if (!original || !draft || !changes || !settlement || blockers.length > 0 || submitting) return;
    if (settlement.kind === "charge" && !chargeMethod) return; // default not resolved yet

    const customerName = capitalizeWords(`${draft.firstName} ${draft.lastName}`.trim());
    const lines = [];
    if (settlement.kind === "charge") {
      const methodLabel =
        chargeMethod === "saved_card"
          ? `charge the saved card ····${profile.savedCard?.last4}`
          : chargeMethod === "cash"
            ? "be collected in CASH at the register"
            : "be requested via a Stripe payment link";
      lines.push(`💳 Additional ${formatCentsAsUSD(settlement.chargeCents)} will ${methodLabel}.`);
      if (chargeMethod === "cash") lines.push("Collect the cash BEFORE confirming this dialog.");
    } else if (settlement.kind === "refund") {
      if (allocation.cardCents > 0) lines.push(`💳 ${formatCentsAsUSD(allocation.cardCents)} refunds to the card via Stripe.`);
      if (allocation.cashCents > 0) lines.push(`💵 ${formatCentsAsUSD(allocation.cashCents)} is returned in CASH from the register.`);
    } else if (settlement.kind === "reprice") {
      lines.push(`Unpaid booking — total updates to ${formatCentsAsUSD(settlement.newTotalAmountCents)}. No payment is processed.`);
    } else {
      lines.push("No payment change.");
    }
    const choice = await ask({
      title: `Save changes to ${customerName}'s booking?`,
      lines,
      actions: [
        { label: "Save & notify customer", value: "notify", variant: "primary" },
        { label: "Save without notifying", value: "silent", variant: "secondary" },
        { label: "Cancel", value: null, variant: "ghost" },
      ],
      footnote: "Notify sends the booking-update email and text. Payment receipts and refund notices are sent either way.",
    });
    if (!choice) return;
    await performCommit({ notify: choice === "notify" });
  };

  // Runs once staff picked an option in the dialog. `notify` decides whether the
  // customer gets the standard edited-booking email + SMS; money receipts
  // (card charge, refund, payment link) are sent by their own flows regardless.
  const performCommit = async ({ notify }) => {
    if (!original || !draft || !changes || !settlement || blockers.length > 0 || submitting) return;
    const customerName = capitalizeWords(`${draft.firstName} ${draft.lastName}`.trim());

    setSubmitting(true);
    let moneyMoved = false; // true once a charge/refund/cash collection happened
    try {
      const tenantId = original.tenantId || null;
      const storeId = original.storeId || null;
      const bookingsPath =
        tenantId && storeId ? `tenants/${tenantId}/stores/${storeId}/bookings` : getCollectionPath("bookings");
      const bookingRef = doc(db, bookingsPath, bookingId);

      // 1. Concurrency guard: refuse to commit over a version we did not edit.
      const freshSnap = await getDoc(bookingRef);
      if (!freshSnap.exists()) throw new Error("Booking no longer exists");
      const freshData = freshSnap.data();
      const freshMs = parseRentalDate(freshData.updatedAt)?.getTime() ?? null;
      const snapMs = parseRentalDate(original.updatedAt)?.getTime() ?? null;
      if (freshMs !== snapMs) {
        setSubmitting(false);
        await notice("Booking changed elsewhere", [
          "This booking was changed by someone else while you were editing (another device, a payment, or a signed waiver).",
          "",
          "Your edits were NOT saved. The booking has been reloaded — please review and try again.",
        ]);
        setOriginal(freshData);
        setDraft(makeDraft(freshData, products1, products2));
        setScreen("hub");
        return;
      }

      // 2. Availability backstop, same pattern as the cash desk: staff edits
      // REFUSE on shortage rather than oversell — no money is captured yet.
      const shortagesNow = findDraftShortages({
        draft,
        lineItems,
        bookings: otherBookings,
        pendingBookings,
        effectiveProducts1: effective.products1,
        effectiveProducts2: effective.products2,
        excludeBookingId: bookingId,
      });
      if (shortagesNow.length > 0) {
        setSubmitting(false);
        await notice("Cannot save", [
          "These items were just taken by another booking:",
          "",
          ...shortagesNow.map((s) => `• ${s.name}: need ${s.wanted}, ${s.available} available`),
        ]);
        return;
      }

      // 3. Move the money. Failures abort BEFORE the booking is touched.
      const paymentEntries = [];
      const refundEntries = [];
      let paymentLinkUrl = null;

      if (settlement.kind === "charge") {
        if (chargeMethod === "saved_card") {
          const sourcePayment = [...(original.payments || [])]
            .reverse()
            .find((payment) => payment?.stripeCustomerId && payment?.cardInfo?.last4);
          if (!sourcePayment) throw new Error("No saved card found on this booking");
          const chargeSavedCard = httpsCallable(functions, "chargeSavedCard");
          const result = await chargeSavedCard({
            bookingId,
            amountCents: settlement.chargeCents,
            description: `Booking Changes - ${bookingId}`,
            tenantId,
            storeId,
            last4: sourcePayment.cardInfo.last4,
            testMode: useTestStripe,
          });
          if (result.data?.status !== "succeeded") {
            throw new Error(`Payment failed: ${result.data?.status || "unknown"}`);
          }
          moneyMoved = true;
          paymentEntries.push(
            buildCardChargeEntry({
              amountCents: settlement.chargeCents,
              paymentIntentId: result.data?.paymentIntentId || null,
              sourcePayment,
              lineItems,
            })
          );
        } else if (chargeMethod === "cash") {
          moneyMoved = true; // staff confirmed the cash was collected
          paymentEntries.push(
            buildCashChargeEntry({ amountCents: settlement.chargeCents, lineItems })
          );
        } else {
          // Payment link: the edit saves now; the webhook appends the payment
          // entry when the link is paid (functions/index.js:3916).
          const createPaymentLink = httpsCallable(functions, "createPaymentLink");
          const linkResult = await createPaymentLink({
            amountCents: settlement.chargeCents,
            description: `Booking Changes - ${bookingId}`,
            customerEmail: draft.customerEmail,
            customerPhone: draft.customerPhone,
            customerName,
            saveCard: true,
            bookingId,
            isBookingEdit: true,
            skipBookingConfirmation: true,
            testMode: useTestStripe,
          });
          if (!linkResult.data?.url) throw new Error("Failed to create payment link");
          paymentLinkUrl = linkResult.data.url;
        }
      } else if (settlement.kind === "refund") {
        if (allocation.cardCents > 0) {
          const processRefund = httpsCallable(functions, "processRefund");
          const refundResult = await processRefund({
            bookingId,
            amountCents: allocation.cardCents,
            reason: "booking_edit",
            tenantId,
            storeId,
            testMode: useTestStripe,
            sendReceipt: true,
            // We append the canonical (cents) refund entry ourselves below.
            skipRefundEntry: true,
          });
          if (!refundResult.data?.success) {
            throw new Error(refundResult.data?.error || "Stripe refund failed");
          }
          moneyMoved = true;
          refundEntries.push(
            buildCardRefundEntry({
              amountCents: allocation.cardCents,
              stripeRefundId: refundResult.data?.refundId || null,
              reason: "booking_edit",
            })
          );
        }
        if (allocation.cashCents > 0) {
          moneyMoved = true; // staff confirmed the cash will be handed over
          refundEntries.push(
            buildCashRefundEntry({ amountCents: allocation.cashCents, reason: "booking_edit" })
          );
        }
      }

      // 4. ONE write with the fully re-derived document.
      const fields = buildUpdatedBookingFields({ booking: original, draft, lineItems, quote, settlement });
      const selectedUser = useAuthStore.getState().selectedUser;
      const changeEntry = selectedUser
        ? createChangeTrackingEntry(
            { userId: selectedUser.id, firstName: selectedUser.firstName || "", lastName: selectedUser.lastName || "" },
            {
              changeType: settlement.kind === "refund" ? "refund" : settlement.kind === "charge" ? "charge" : "edit",
              fieldChanged: "booking_details",
              description:
                settlement.kind === "refund"
                  ? `Edit wizard: refunded ${formatCentsAsUSD(settlement.refundCents)}`
                  : settlement.kind === "charge"
                    ? `Edit wizard: charged ${formatCentsAsUSD(settlement.chargeCents)}${paymentLinkUrl ? " (payment link)" : ""}`
                    : "Edit wizard: booking details updated",
              timestamp: Timestamp.now(),
            }
          )
        : null;

      const updatePayload = { ...fields };
      if (paymentEntries.length > 0) updatePayload.payments = arrayUnion(...paymentEntries);
      if (refundEntries.length > 0) updatePayload.refunds = arrayUnion(...refundEntries);
      if (changeEntry) updatePayload.editedBy = arrayUnion(changeEntry);
      await updateDoc(bookingRef, updatePayload);

      // 5. Standard edited-booking email + SMS, only when staff chose to notify.
      // Non-blocking: money has moved and the booking is saved — a notification
      // failure must not undo that.
      if (notify) {
        try {
          const notifChanges = buildNotificationChanges({
            booking: original,
            draft,
            lineItems,
            quote,
            settlement,
            effectiveProducts2: effective.products2,
          });
          const sendEditedBookingNotification = httpsCallable(functions, "sendEditedBookingNotification");
          await sendEditedBookingNotification(
            sanitizeForCallable({
              bookingId,
              tenantId,
              storeId,
              quantityChanges: buildQuantityChanges({ booking: original, lineItems }),
              dateChanges: changes.datesChanged
                ? { newStartDate: draft.startDate, newEndDate: draft.endDate }
                : null,
              addressChanges:
                changes.transportChanged && draft.addressInput && draft.miles != null
                  ? { newAddress: draft.addressInput, newDistance: draft.miles }
                  : null,
              deliveryChanges: changes.transportChanged
                ? {
                    delivery: draft.deliveryNeeded,
                    homePickup: draft.homePickupNeeded,
                    inStorePickup: !(draft.deliveryNeeded && draft.homePickupNeeded),
                    // The cloud function re-applies these — pass the exact values the
                    // updateDoc above wrote (community fold included) so its second
                    // write is a no-op rather than an unfold.
                    deliveryAddress: fields.deliveryAddress,
                    gatedCommunity: draft.gatedCommunity,
                    communityName: draft.communityName,
                    addressNotes: fields.addressNotes,
                  }
                : null,
              changes: notifChanges,
              paymentMethod:
                settlement.kind === "charge"
                  ? chargeMethod === "saved_card" ? "existing" : chargeMethod === "link" ? "new" : "cash"
                  : "existing",
              // The booking document and the money entries were already written
              // above in their canonical (cents) shapes.
              skipMoneyEntries: true,
            })
          );
        } catch (notifError) {
          console.error("⚠️ Edited-booking notification failed (edit is saved):", notifError);
        }
      }

      // 6. Hand off.
      if (paymentLinkUrl) {
        const phone = draft.customerPhone.replace(/\D/g, "");
        setSubmitting(false);
        if (
          phone &&
          (await confirm("Text the payment link?", [`Send the Stripe payment link to ${customerName} at ${draft.customerPhone}.`], {
            okLabel: "Text the link",
            cancelLabel: "Skip",
          }))
        ) {
          try {
            const sendPaymentLinkSms = httpsCallable(functions, "sendPaymentLinkSms");
            await sendPaymentLinkSms({
              bookingId,
              paymentUrl: paymentLinkUrl,
              customerPhone: phone,
              customerName,
              tenantId,
              storeId,
            });
            await notice("Payment link sent", ["The customer was texted the Stripe payment link."]);
          } catch (smsError) {
            console.error("❌ Payment link SMS failed:", smsError);
            await notice("Could not send the SMS", ["The payment link opens next so you can share it another way."]);
          }
        }
        navigate("/stripe-checkout", {
          state: { paymentUrl: paymentLinkUrl, bookingId, referrer: `/view-booking/${bookingId}` },
        });
        return;
      }

      const doneLines = [];
      if (settlement.kind === "charge") doneLines.push(`Charged: ${formatCentsAsUSD(settlement.chargeCents)}`);
      if (settlement.kind === "refund") doneLines.push(`Refunded: ${formatCentsAsUSD(settlement.refundCents)}`);
      doneLines.push(notify ? "The customer has been notified by email and SMS." : "The customer was not notified.");
      setSubmitting(false);
      await notice("✅ Booking updated", doneLines);
      navigate(`/view-booking/${bookingId}`);
    } catch (error) {
      console.error("❌ Edit commit failed:", error);
      if (moneyMoved) {
        // The charge/refund went through but the booking write (or a later step)
        // failed. Retrying the payment would move the money twice.
        setSubmitting(false);
        await notice(
          "⚠️ Payment went through, but the save failed",
          [
            `Saving the booking failed: ${error.message || "Unknown error"}`,
            "",
            "Do NOT retry the charge or refund. Open the booking, verify its payment history against Stripe/the till, and reconcile manually if needed.",
          ],
          { danger: true }
        );
      } else {
        setSubmitting(false);
        await notice("Could not save the changes", [error.message || "Unknown error", "", "The booking was not modified."]);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // --- Render ----------------------------------------------------------------

  if (!authChecked) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }
  if (!isAuthenticated) return null; // redirecting

  const header = (
    <AuthHeader
      title="Edit Booking Wizard"
      subtitle={settings?.storeName?.name ? `by ${settings.storeName.name}` : ""}
      isDesktop={isDesktop}
      isAuthenticated={isAuthenticated}
      storeLogo={settings?.storeLogo?.url ?? ""}
      showHelperBar={false}
    />
  );

  // Picker mode — no booking chosen yet.
  if (!bookingId) {
    return (
      <View style={styles.container}>
        {header}
        <View style={isDesktop ? styles.desktopWrapper : styles.mobileWrapper}>
          <BookingPicker
            bookings={bookings}
            onPick={(id) => navigate(`/edit-booking-wizard/${id}`)}
          />
        </View>
      </View>
    );
  }

  if (loadFailed) {
    return (
      <View style={styles.container}>
        {header}
        <View style={styles.centered}>
          <Text style={styles.errorTitle}>Booking not found</Text>
          <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.primaryButton} onPress={() => navigate("/edit-booking-wizard")}>
            <Text style={styles.primaryButtonText}>Pick another booking</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  if (!original || !draft || !changes || !settlement) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.loadingText}>Loading booking…</Text>
      </View>
    );
  }

  if (original.cancelled === true || original.status === "cancelled") {
    return (
      <View style={styles.container}>
        {header}
        <View style={styles.centered}>
          <Text style={styles.errorTitle}>This booking is cancelled</Text>
          <Text style={styles.errorText}>Cancelled bookings cannot be edited.</Text>
          <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.primaryButton} onPress={() => navigate(`/view-booking/${bookingId}`)}>
            <Text style={styles.primaryButtonText}>View booking</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  const customerName = capitalizeWords(
    `${draft.firstName || ""} ${draft.lastName || ""}`.trim()
  );

  const sectionMeta = [
    {
      key: "dates",
      icon: "🗓️",
      title: "Rental dates",
      changed: changes.datesChanged,
      summary: fmtRange(draft.startDate, draft.endDate),
    },
    {
      key: "products",
      icon: "🚲",
      title: `${cfg.product1Title} & ${cfg.product2Title}`,
      changed: changes.itemsChanged || changes.rateOverrideChanged,
      summary:
        [...lineItems.product1, ...lineItems.product2]
          .map((item) => `${item.quantity}× ${item.name}`)
          .join(", ") || "No items",
    },
    {
      key: "transport",
      icon: "🚚",
      title: "Transportation",
      changed: changes.transportChanged,
      summary: [
        draft.deliveryNeeded ? "Delivery" : "Shop pickup",
        draft.homePickupNeeded ? "Home pickup" : "Shop drop-off",
        draft.miles != null && (draft.deliveryNeeded || draft.homePickupNeeded)
          ? `${draft.miles} mi`
          : null,
      ]
        .filter(Boolean)
        .join(" · "),
    },
    {
      key: "customer",
      icon: "👤",
      title: "Customer info",
      changed: changes.customerChanged,
      summary: `${customerName || "—"} · ${draft.customerEmail || "no email"}`,
    },
  ];

  const settlementChip = () => {
    if (!changes.anyChange) return null;
    if (settlement.kind === "charge") {
      return (
        <Text style={[styles.deltaChip, styles.deltaCharge]}>
          Customer owes {formatCentsAsUSD(settlement.chargeCents)}
        </Text>
      );
    }
    if (settlement.kind === "refund") {
      return (
        <Text style={[styles.deltaChip, styles.deltaRefund]}>
          Refund {formatCentsAsUSD(settlement.refundCents)}
        </Text>
      );
    }
    if (settlement.kind === "reprice") {
      return (
        <Text style={[styles.deltaChip, styles.deltaNeutral]}>
          New total {formatCentsAsUSD(settlement.newTotalAmountCents)} (unpaid booking)
        </Text>
      );
    }
    return <Text style={[styles.deltaChip, styles.deltaNeutral]}>No payment change</Text>;
  };

  // Re-send the standard booking info email (the one auto-sent on completion).
  // Sends to the email stored on the booking doc, not unsaved draft edits.
  const handleResendConfirmation = async () => {
    const email = original?.customerEmail;
    if (!email) {
      await notice("No email on file", ["This booking has no customer email on file."]);
      return;
    }
    if (!(await confirm("Re-send the booking info email?", [`It will go to ${email}.`], { okLabel: "Send email" }))) return;
    try {
      setResendState("sending");
      const resendBookingConfirmation = httpsCallable(functions, "resendBookingConfirmation");
      const result = await resendBookingConfirmation({ bookingId });
      if (result.data?.success) {
        setResendState("sent");
        setTimeout(() => setResendState("idle"), 4000);
      } else {
        setResendState("idle");
        await notice("Email not sent", [result.data?.error || "Could not send the email."]);
      }
    } catch (resendError) {
      console.error("❌ Resend booking email failed:", resendError);
      setResendState("idle");
      await notice("Email not sent", [resendError.message || "Could not send the email."]);
    }
  };

  // ---- Hub ----
  const renderHub = () => (
    <ScrollView style={styles.content}>
      <View style={styles.bookingCard}>
        <View style={styles.bookingCardInfo}>
          <Text style={styles.bookingName}>{customerName || "Unknown customer"}</Text>
          <Text style={styles.bookingMeta}>
            #{original.customerBookingID || "—"} · booked {fmtDay(original.createdAt)}
          </Text>
          <Text style={styles.bookingMeta}>
            Current total {formatCentsAsUSD(original.totalAmount || 0)}
            {profile?.cashPaidCents > 0 && profile?.cardPaidCents > 0
              ? " · paid card + cash"
              : profile?.cashPaidCents > 0
                ? " · paid in cash"
                : profile?.cardPaidCents > 0
                  ? " · paid by card"
                  : " · unpaid"}
          </Text>
        </View>
        <View
          style={styles.resendWrap}
          onMouseEnter={CAN_HOVER ? () => setShowResendTip(true) : undefined}
          onMouseLeave={CAN_HOVER ? () => setShowResendTip(false) : undefined}
        >
          <TouchableOpacity
            dataSet={{ hover: "lift" }}
            style={styles.resendButton}
            onPress={handleResendConfirmation}
            onLongPress={showResendTipBriefly}
            disabled={resendState !== "idle"}
          >
            {resendState === "sending" ? (
              <ActivityIndicator size="small" color={colors.surface} />
            ) : (
              <Text style={styles.resendButtonText}>
                {resendState === "sent" ? "✓ Sent" : "📧 Resend booking email"}
              </Text>
            )}
          </TouchableOpacity>
          {showResendTip && (
            <View style={styles.resendTip} pointerEvents="none">
              <View style={styles.resendTipArrow} />
              <Text style={styles.resendTipText}>{RESEND_TIP}</Text>
            </View>
          )}
        </View>
      </View>

      {changedElsewhere && (
        <View style={styles.warnBanner}>
          <Text style={styles.warnBannerText}>
            ⚠️ This booking just changed somewhere else (payment, waiver or another edit). Saving
            will be blocked — discard your changes to reload the latest version.
          </Text>
        </View>
      )}

      <Text style={styles.hubPrompt}>What would you like to change?</Text>

      {sectionMeta.map((section) => (
        <TouchableOpacity
          key={section.key}
          dataSet={{ hover: "lift" }}
          style={[styles.sectionCard, section.changed && styles.sectionCardChanged]}
          onPress={() => openSection(section.key)}
        >
          <Text style={styles.sectionCardIcon}>{section.icon}</Text>
          <View style={styles.sectionCardBody}>
            <View style={styles.sectionCardTitleRow}>
              <Text style={styles.sectionCardTitle}>{section.title}</Text>
              {section.changed && <Text style={styles.changedBadge}>changed</Text>}
            </View>
            <Text style={styles.sectionCardSummary} numberOfLines={2}>{section.summary}</Text>
          </View>
          <Text style={styles.sectionCardChevron}>›</Text>
        </TouchableOpacity>
      ))}

      {blockers.length > 0 && changes.anyChange && (
        <View style={styles.blockerBox}>
          <Text style={styles.blockerTitle}>Before this can be saved:</Text>
          {blockers.map((blocker) => (
            <Text key={blocker} style={styles.blockerText}>• {blocker}</Text>
          ))}
        </View>
      )}

      {changes.anyChange && (
        <View style={styles.hubFooter}>
          {settlementChip()}
          <TouchableOpacity
            dataSet={{ hover: "lift" }}
            style={[styles.primaryButton, blockers.length > 0 && styles.buttonDisabled]}
            onPress={() => setScreen("review")}
            disabled={blockers.length > 0}
          >
            <Text style={styles.primaryButtonText}>Review & save changes</Text>
          </TouchableOpacity>
          <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.ghostButton} onPress={discardAll}>
            <Text style={styles.ghostButtonText}>Discard all changes</Text>
          </TouchableOpacity>
        </View>
      )}

      {!changes.anyChange && (
        <Text style={styles.noChangesHint}>No changes yet — open a section above to begin.</Text>
      )}
      <View style={styles.bottomSpacer} />
    </ScrollView>
  );

  // ---- Section screens ----
  const renderSection = () => (
    <>
      <ScrollView style={styles.content}>
        {screen === "dates" && (
          <>
            {dateFee.feeCents > 0 && (
              <View style={styles.warnBanner}>
                <Text style={styles.warnBannerText}>
                  Shortening by {dateFee.daysLost} day{dateFee.daysLost === 1 ? "" : "s"} incurs a{" "}
                  {formatCentsAsUSD(dateFee.feeCents)} date-change fee under the cancellation policy.
                </Text>
              </View>
            )}
            <StepDates
              startDate={startDate}
              endDate={endDate}
              currentMonth={currentMonth}
              onSelectDate={handleSelectDate}
              onMonthChange={(month) => updateDraft({ currentMonth: month.toISOString() })}
              bookings={otherBookings}
              pendingBookings={pendingBookings}
              holdTick={holdTick}
              products1={effective.products1}
              product1Title={cfg.product1Title}
              availability={availability}
              isAuthenticated={isAuthenticated}
              isDesktop={isDesktop}
            />
          </>
        )}

        {screen === "products" && (
          <StepProducts
            products1={effective.products1}
            products2={effective.products2}
            product1Title={cfg.product1Title}
            product2Title={cfg.product2Title}
            selectedBikes={draft.selectedBikes}
            selectedAddOns={draft.selectedAddOns}
            onBikeChange={handleBikeChange}
            onAddOnChange={handleAddOnChange}
            availability={availability}
            totalBikes={lineItems.totalBikes}
            isAuthenticated={isAuthenticated}
            dailyRateOverrideCents={draft.dailyRateOverrideCents}
            onRateOverrideChange={(cents) => updateDraft({ dailyRateOverrideCents: cents })}
          />
        )}

        {screen === "transport" && (
          <StepTransportation
            delivery={draft.deliveryNeeded}
            homePickup={draft.homePickupNeeded}
            inStorePickup={draft.inStorePickup}
            addressInput={draft.addressInput}
            addressConfirmed={draft.addressConfirmed}
            gatedCommunity={draft.gatedCommunity}
            communityName={draft.communityName}
            addressNotes={draft.addressNotes}
            miles={draft.miles}
            quote={quote}
            settings={settings}
            onChange={updateDraft}
          />
        )}

        {screen === "customer" && (
          <StepCustomerInfo
            firstName={draft.firstName}
            lastName={draft.lastName}
            customerEmail={draft.customerEmail}
            customerPhone={draft.customerPhone}
            onChange={updateDraft}
          />
        )}

        <WizardSummary
          draft={draft}
          quote={quote}
          products2={effective.products2}
          product1Title={cfg.product1Title}
          product2Title={cfg.product2Title}
          useDurationDiscounts={cfg.useDurationDiscounts}
          durationDiscounts={cfg.durationDiscounts}
        />
        <View style={styles.bottomSpacer} />
      </ScrollView>

      <View style={styles.navBar}>
        <TouchableOpacity dataSet={{ hover: "lift" }} style={[styles.navButton, styles.backButton]} onPress={cancelSection}>
          <Text style={styles.backButtonText}>Cancel</Text>
        </TouchableOpacity>
        <TouchableOpacity dataSet={{ hover: "lift" }} style={[styles.navButton, styles.nextButton]} onPress={applySection}>
          <Text style={styles.nextButtonText}>Apply changes</Text>
        </TouchableOpacity>
      </View>
    </>
  );

  // ---- Review ----
  const notifPreview =
    changes.anyChange && screen === "review"
      ? buildNotificationChanges({
          booking: original,
          draft,
          lineItems,
          quote,
          settlement,
          effectiveProducts2: effective.products2,
        })
      : null;

  const renderReview = () => (
    <>
      <ScrollView style={styles.content}>
        {/* What changed */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Changes</Text>

          {changes.datesChanged && (
            <View style={styles.reviewRow}>
              <Text style={styles.reviewLabel}>Dates</Text>
              <Text style={styles.reviewOld}>{fmtRange(original.startDate, original.endDate)}</Text>
              <Text style={styles.reviewNew}>→ {fmtRange(draft.startDate, draft.endDate)}</Text>
            </View>
          )}

          {notifPreview?.addedItems.map((item, index) => (
            <View key={`add-${index}`} style={styles.reviewLineRow}>
              <Text style={styles.reviewAdded}>＋ {item.quantity}× {item.name}</Text>
              <Text style={styles.reviewAdded}>{`$${(item.quantity * item.price).toFixed(2)}`}</Text>
            </View>
          ))}
          {notifPreview?.removedItems.map((item, index) => (
            <View key={`rem-${index}`} style={styles.reviewLineRow}>
              <Text style={styles.reviewRemoved}>－ {item.quantity}× {item.name}</Text>
              <Text style={styles.reviewRemoved}>{`$${(item.quantity * item.price).toFixed(2)}`}</Text>
            </View>
          ))}

          {changes.transportChanged && (
            <View style={styles.reviewRow}>
              <Text style={styles.reviewLabel}>Transportation</Text>
              <Text style={styles.reviewOld}>
                {[original.delivery ? "Delivery" : "Shop pickup", original.homePickup ? "Home pickup" : "Shop drop-off"].join(" · ")}
                {original.deliveryAddress ? ` · ${original.deliveryAddress}` : ""}
              </Text>
              <Text style={styles.reviewNew}>
                → {[draft.deliveryNeeded ? "Delivery" : "Shop pickup", draft.homePickupNeeded ? "Home pickup" : "Shop drop-off"].join(" · ")}
                {draft.addressInput && (draft.deliveryNeeded || draft.homePickupNeeded) ? ` · ${draft.addressInput}` : ""}
              </Text>
            </View>
          )}

          {changes.customerChanged && (
            <View style={styles.reviewRow}>
              <Text style={styles.reviewLabel}>Customer</Text>
              <Text style={styles.reviewOld}>
                {capitalizeWords(`${original.firstName || ""} ${original.lastName || ""}`.trim())} · {original.customerEmail} · {original.customerPhone}
              </Text>
              <Text style={styles.reviewNew}>
                → {customerName} · {draft.customerEmail} · {draft.customerPhone}
              </Text>
            </View>
          )}
        </View>

        {/* Money */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Payment</Text>

          <View style={styles.moneyRow}>
            <Text style={styles.moneyLabel}>Current total</Text>
            <Text style={styles.moneyValue}>{formatCentsAsUSD(settlement.prevTotalCents)}</Text>
          </View>
          {changes.priceAffecting && (
            <View style={styles.moneyRow}>
              <Text style={styles.moneyLabel}>New total (repriced)</Text>
              <Text style={styles.moneyValue}>{formatCentsAsUSD(settlement.newQuoteTotalCents)}</Text>
            </View>
          )}
          {settlement.dateChangeFeeCents > 0 && (
            <View style={styles.moneyRow}>
              <Text style={styles.moneyLabel}>Date change fee (kept)</Text>
              <Text style={styles.moneyValue}>{formatCentsAsUSD(settlement.dateChangeFeeCents)}</Text>
            </View>
          )}
          {settlement.kind === "refund" && settlement.processingFeeCents > 0 && (
            <View style={styles.moneyRow}>
              <Text style={styles.moneyLabel}>Refund processing fee ({refundProcessingFeePercent}%)</Text>
              <Text style={styles.moneyValue}>−{formatCentsAsUSD(settlement.processingFeeCents)}</Text>
            </View>
          )}

          <View style={styles.moneyDivider} />
          <View style={styles.moneyRow}>
            <Text style={styles.moneyTotalLabel}>
              {settlement.kind === "charge"
                ? "Charge customer"
                : settlement.kind === "refund"
                  ? "Refund customer"
                  : settlement.kind === "reprice"
                    ? "New total owed (unpaid booking)"
                    : "No payment change"}
            </Text>
            <Text
              style={[
                styles.moneyTotalValue,
                settlement.kind === "charge" && { color: colors.warningInk },
                settlement.kind === "refund" && { color: colors.danger },
              ]}
            >
              {settlement.kind === "charge"
                ? formatCentsAsUSD(settlement.chargeCents)
                : settlement.kind === "refund"
                  ? formatCentsAsUSD(settlement.refundCents)
                  : settlement.kind === "reprice"
                    ? formatCentsAsUSD(settlement.newTotalAmountCents)
                    : "—"}
            </Text>
          </View>

          {/* Amount-owed override. The page bounces unauthenticated visitors at
              line 281, so reaching this at all means staff -- no extra gate.
              Keyed off the PRE-override base, not settlement.kind: overriding a
              charge to $0 collapses kind to "none", and gating on kind would
              make the control vanish the moment it was used, with no way back. */}
          {(settlement.baseChargeCents > 0 || settlement.kind === "reprice") && (
            <View style={styles.methodBlock}>
              <Text style={styles.methodHeading}>Amount owed</Text>
              {radioRow(
                !owedOverrideOn,
                `Calculated amount (${formatCentsAsUSD(owedBaseCents)})`,
                () => setOwedOverrideOn(false)
              )}
              {radioRow(owedOverrideOn, "Override the amount owed", () => setOwedOverrideOn(true))}
              {owedOverrideOn && (
                <View>
                  <View style={styles.overrideInputRow}>
                    <Text style={styles.overrideSuffix}>$</Text>
                    <TextInput
                      style={styles.overrideInput}
                      value={owedOverrideText}
                      onChangeText={(text) => setOwedOverrideText(text.replace(/[^0-9.]/g, ""))}
                      placeholder="0.00"
                      placeholderTextColor={colors.ink300}
                      keyboardType="decimal-pad"
                      inputMode="decimal"
                    />
                  </View>
                  <Text style={styles.overrideHint}>
                    Anything from $0.00 up to {formatCentsAsUSD(owedBaseCents)} — $0.00 waives it
                    entirely. Higher than that is refused, not rounded down.
                  </Text>
                </View>
              )}
            </View>
          )}

          {/* Charge method */}
          {settlement.kind === "charge" && (
            <View style={styles.methodBlock}>
              <Text style={styles.methodHeading}>How will the difference be collected?</Text>
              {profile.savedCard && radioRow(
                chargeMethod === "saved_card",
                `Charge saved card ${profile.savedCard.brand ? profile.savedCard.brand + " " : ""}····${profile.savedCard.last4}`,
                () => setChargeMethod("saved_card")
              )}
              {radioRow(
                chargeMethod === "cash",
                "Collect cash at the register",
                () => setChargeMethod("cash"),
                "Take the cash before saving — the booking records it as paid."
              )}
              {radioRow(
                chargeMethod === "link",
                "Stripe payment link",
                () => setChargeMethod("link"),
                "The edit saves now; the link is opened and can be texted to the customer."
              )}
            </View>
          )}

          {/* Refund breakdown & overrides */}
          {settlement.kind === "refund" && (
            <View style={styles.methodBlock}>
              <Text style={styles.methodHeading}>Refund amount</Text>
              {radioRow(refundMode === "auto", "Calculated amount (fees applied)", () => setRefundMode("auto"))}
              {radioRow(refundMode === "percent", "Percent of the difference", () => setRefundMode("percent"))}
              {refundMode === "percent" && (
                <View style={styles.overrideInputRow}>
                  <TextInput
                    style={styles.overrideInput}
                    value={refundPercentText}
                    onChangeText={(text) => setRefundPercentText(text.replace(/[^0-9.]/g, ""))}
                    keyboardType="decimal-pad"
                    inputMode="decimal"
                  />
                  <Text style={styles.overrideSuffix}>% of {formatCentsAsUSD(settlement.baseRefundCents || 0)}</Text>
                </View>
              )}
              {radioRow(refundMode === "custom", "Custom amount", () => setRefundMode("custom"))}
              {refundMode === "custom" && (
                <View style={styles.overrideInputRow}>
                  <Text style={styles.overrideSuffix}>$</Text>
                  <TextInput
                    style={styles.overrideInput}
                    value={refundCustomText}
                    onChangeText={setRefundCustomText}
                    placeholder="0.00"
                    placeholderTextColor={colors.ink300}
                    keyboardType="decimal-pad"
                    inputMode="decimal"
                  />
                </View>
              )}

              <Text style={styles.methodHeading}>Where it goes</Text>
              {allocation?.cardCents > 0 && (
                <Text style={styles.allocationLine}>
                  💳 {formatCentsAsUSD(allocation.cardCents)} back to the card (Stripe)
                </Text>
              )}
              {allocation?.cashCents > 0 && (
                <Text style={styles.allocationLine}>
                  💵 {formatCentsAsUSD(allocation.cashCents)} in cash from the register
                </Text>
              )}
              {allocation?.shortfallCents > 0 && (
                <Text style={styles.allocationShort}>
                  ⚠️ {formatCentsAsUSD(allocation.shortfallCents)} cannot be refunded — it was never
                  collected. Lower the amount with a custom override.
                </Text>
              )}
              {profile.cardAvailableCents > 0 && profile.cashAvailableCents > 0 && (
                <TouchableOpacity
                  dataSet={{ hover: "lift" }}
                  style={styles.preferToggle}
                  onPress={() => setPreferCashRefund((value) => !value)}
                >
                  <Text style={styles.preferToggleText}>
                    {preferCashRefund ? "◉" : "◯"} Prefer cash first (card money always returns to the card)
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {settlement.kind === "reprice" && (
            <Text style={styles.repriceHint}>
              This booking has no recorded payments, so nothing is charged or refunded — the amount
              owed simply updates.
            </Text>
          )}
        </View>

        {blockers.length > 0 && (
          <View style={styles.blockerBox}>
            {blockers.map((blocker) => (
              <Text key={blocker} style={styles.blockerText}>• {blocker}</Text>
            ))}
          </View>
        )}

        <View style={styles.bottomSpacer} />
      </ScrollView>

      <View style={styles.navBar}>
        <TouchableOpacity dataSet={{ hover: "lift" }} style={[styles.navButton, styles.backButton]} onPress={() => setScreen("hub")}>
          <Text style={styles.backButtonText}>Back</Text>
        </TouchableOpacity>
        <TouchableOpacity
          dataSet={{ hover: "lift" }}
          style={[styles.navButton, styles.saveButton, (blockers.length > 0 || submitting) && styles.buttonDisabled]}
          onPress={handleCommit}
          disabled={blockers.length > 0 || submitting}
        >
          <Text style={styles.nextButtonText}>
            {settlement.kind === "charge"
              ? `Save & collect ${formatCentsAsUSD(settlement.chargeCents)}`
              : settlement.kind === "refund"
                ? `Save & refund ${formatCentsAsUSD(settlement.refundCents)}`
                : "Save changes"}
          </Text>
        </TouchableOpacity>
      </View>
    </>
  );

  return (
    <View style={styles.container}>
      {header}
      <View style={isDesktop ? styles.desktopWrapper : styles.mobileWrapper}>
        {screen === "hub" && renderHub()}
        {["dates", "products", "transport", "customer"].includes(screen) && renderSection()}
        {screen === "review" && renderReview()}
      </View>

      <Modal visible={submitting} transparent animationType="fade">
        <View style={styles.submittingOverlay}>
          <ActivityIndicator size="large" color="#FFFFFF" />
          <Text style={styles.submittingText}>Saving changes…</Text>
        </View>
      </Modal>
      {dialog}
    </View>
  );
};

// availability wrapper kept out of the component so the useMemo above stays readable
const findAvailability = ({ draft, otherBookings, pendingBookings, effective, bookingId }) =>
  computeAvailabilityForRange({
    startDate: draft.startDate ? new Date(draft.startDate) : null,
    endDate: draft.endDate ? new Date(draft.endDate) : null,
    bookings: otherBookings,
    pendingBookings,
    products1Catalog: effective.products1,
    products2Catalog: effective.products2,
    excludeBookingId: bookingId,
  });

// Small radio row used by the review screen.
const radioRow = (selected, label, onPress, sublabel) => (
  <TouchableOpacity key={label} style={styles.radioRow} onPress={onPress}>
    <View style={[styles.radio, selected && styles.radioSelected]}>
      {selected && <View style={styles.radioDot} />}
    </View>
    <View style={{ flex: 1 }}>
      <Text style={styles.radioLabel}>{label}</Text>
      {sublabel ? <Text style={styles.radioSublabel}>{sublabel}</Text> : null}
    </View>
  </TouchableOpacity>
);

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.bg, minHeight: "100vh", display: "flex", flexDirection: "column" },
  loadingContainer: { flex: 1, alignItems: "center", justifyContent: "center", minHeight: "100vh" },
  loadingText: { marginTop: 12, color: colors.ink500, fontSize: 14 },
  desktopWrapper: { width: "100%", maxWidth: 800, flex: 1, alignSelf: "center", flexDirection: "column" },
  mobileWrapper: { flex: 1, flexDirection: "column" },
  content: { flex: 1, padding: 16 },
  centered: { padding: 24, alignItems: "center" },
  bottomSpacer: { height: 24 },

  errorTitle: { fontSize: 18, fontWeight: "bold", color: colors.ink700, marginBottom: 8 },
  errorText: { fontSize: 14, color: colors.ink500, textAlign: "center", marginBottom: 16 },

  section: {
    backgroundColor: colors.surface,
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    boxShadow: elevation.sm,
  },
  sectionTitle: { fontSize: 16, fontWeight: "bold", color: colors.ink700, marginBottom: 12 },
  groupHeading: {
    fontSize: 11,
    fontWeight: "700",
    color: colors.ink400,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 8,
  },
  emptyText: { fontSize: 13, color: colors.ink400 },

  // Picker
  searchInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 6,
    padding: 12,
    fontSize: 16,
    backgroundColor: colors.surface,
  },
  pickerRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderSubtle,
    cursor: "pointer",
  },
  pickerName: { fontSize: 15, fontWeight: "600", color: colors.ink900 },
  pickerMeta: { fontSize: 12, color: colors.ink500, marginTop: 2 },
  pickerRight: { alignItems: "flex-end" },
  pickerTotal: { fontSize: 14, fontWeight: "700", color: colors.ink700 },
  pickerMethod: { fontSize: 11, color: colors.ink400, marginTop: 2 },

  // Hub
  bookingCard: {
    backgroundColor: colors.primary,
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    boxShadow: elevation.md,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  bookingCardInfo: { flex: 1, paddingRight: 12 },
  bookingName: { fontSize: 18, fontWeight: "800", color: colors.surface },
  bookingMeta: { fontSize: 12, color: "rgba(255,255,255,0.85)", marginTop: 3 },
  resendButton: {
    backgroundColor: "rgba(255,255,255,0.16)",
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.45)",
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  resendButtonText: { color: colors.surface, fontSize: 12, fontWeight: "700" },
  // zIndex lifts the tip above the cards that follow in the hub.
  resendWrap: { flexShrink: 0, zIndex: 20 },
  resendTip: {
    position: "absolute",
    top: "100%",
    right: 0,
    marginTop: 8,
    width: 230,
    padding: 10,
    borderRadius: 6,
    backgroundColor: colors.ink700,
    boxShadow: elevation.lg,
  },
  resendTipText: { color: colors.surface, fontSize: 12, lineHeight: 17 },
  resendTipArrow: {
    position: "absolute",
    top: -6,
    right: 18,
    width: 0,
    height: 0,
    borderLeftWidth: 6,
    borderRightWidth: 6,
    borderBottomWidth: 6,
    borderLeftColor: "transparent",
    borderRightColor: "transparent",
    borderBottomColor: colors.ink700,
  },
  hubPrompt: { fontSize: 15, fontWeight: "700", color: colors.ink700, marginBottom: 10 },

  sectionCard: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.surface,
    borderRadius: 8,
    padding: 14,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: colors.border,
    cursor: "pointer",
    boxShadow: elevation.sm,
  },
  sectionCardChanged: { borderColor: colors.primary, borderWidth: 2 },
  sectionCardIcon: { fontSize: 22, marginRight: 12 },
  sectionCardBody: { flex: 1 },
  sectionCardTitleRow: { flexDirection: "row", alignItems: "center" },
  sectionCardTitle: { fontSize: 15, fontWeight: "600", color: colors.ink900 },
  changedBadge: {
    marginLeft: 8,
    fontSize: 10,
    fontWeight: "700",
    color: colors.surface,
    backgroundColor: colors.primary,
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    overflow: "hidden",
  },
  sectionCardSummary: { fontSize: 12, color: colors.ink500, marginTop: 3 },
  sectionCardChevron: { fontSize: 22, color: colors.ink300, marginLeft: 8 },

  warnBanner: {
    backgroundColor: colors.warningSoft,
    borderLeftWidth: 4,
    borderLeftColor: colors.warning,
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  warnBannerText: { fontSize: 13, color: colors.warningInk, lineHeight: 18 },

  blockerBox: {
    backgroundColor: colors.warningSoft,
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: colors.warning,
    marginBottom: 12,
  },
  blockerTitle: { fontSize: 13, fontWeight: "700", color: colors.warningInk, marginBottom: 4 },
  blockerText: { fontSize: 12, color: colors.ink700, paddingVertical: 1 },

  hubFooter: { marginTop: 4, marginBottom: 12 },
  deltaChip: {
    alignSelf: "center",
    fontSize: 14,
    fontWeight: "700",
    marginBottom: 10,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    overflow: "hidden",
  },
  deltaCharge: { backgroundColor: colors.warningSoft, color: colors.warningInk },
  deltaRefund: { backgroundColor: colors.dangerSoft, color: colors.danger },
  deltaNeutral: { backgroundColor: colors.primarySoft, color: colors.primaryDark },
  noChangesHint: { fontSize: 13, color: colors.ink400, textAlign: "center", marginTop: 8 },

  primaryButton: {
    backgroundColor: colors.primary,
    borderRadius: 8,
    paddingVertical: 14,
    alignItems: "center",
    cursor: "pointer",
    minHeight: 48,
    justifyContent: "center",
  },
  primaryButtonText: { color: colors.surface, fontSize: 15, fontWeight: "600" },
  ghostButton: { paddingVertical: 12, alignItems: "center", cursor: "pointer" },
  ghostButtonText: { color: colors.danger, fontSize: 14, fontWeight: "600" },
  buttonDisabled: { backgroundColor: colors.disabled },

  // Section nav
  navBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 16,
    backgroundColor: colors.surface,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  navButton: {
    flex: 1,
    borderRadius: 8,
    paddingVertical: 14,
    alignItems: "center",
    cursor: "pointer",
    marginHorizontal: 5,
    minHeight: 48,
    justifyContent: "center",
  },
  backButton: { backgroundColor: colors.surfaceMuted },
  backButtonText: { color: colors.ink700, fontSize: 15, fontWeight: "600" },
  nextButton: { backgroundColor: colors.primary },
  saveButton: { backgroundColor: colors.success, flex: 2 },
  nextButtonText: { color: colors.surface, fontSize: 15, fontWeight: "600" },

  // Review
  reviewRow: { paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: colors.borderSubtle },
  reviewLabel: { fontSize: 11, fontWeight: "700", color: colors.ink400, textTransform: "uppercase", marginBottom: 2 },
  reviewOld: { fontSize: 13, color: colors.ink500, textDecorationLine: "line-through" },
  reviewNew: { fontSize: 13, color: colors.ink900, fontWeight: "600", marginTop: 2 },
  reviewLineRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 4,
  },
  reviewAdded: { fontSize: 13, color: colors.success, fontWeight: "500" },
  reviewRemoved: { fontSize: 13, color: colors.danger, fontWeight: "500" },

  moneyRow: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 4 },
  moneyLabel: { fontSize: 13, color: colors.ink500, flexShrink: 1, paddingRight: 10 },
  moneyValue: { fontSize: 13, color: colors.ink700, fontWeight: "600" },
  moneyDivider: { height: 1, backgroundColor: colors.borderSubtle, marginVertical: 8 },
  moneyTotalLabel: { fontSize: 15, fontWeight: "700", color: colors.ink900, flexShrink: 1, paddingRight: 10 },
  moneyTotalValue: { fontSize: 18, fontWeight: "800", color: colors.primary },

  methodBlock: { marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: colors.borderSubtle },
  methodHeading: { fontSize: 13, fontWeight: "700", color: colors.ink700, marginBottom: 6, marginTop: 6 },
  radioRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingVertical: 10,
    minHeight: 44,
    cursor: "pointer",
  },
  radio: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: colors.border,
    marginRight: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  radioSelected: { borderColor: colors.primary },
  radioDot: { width: 11, height: 11, borderRadius: 5, backgroundColor: colors.primary },
  radioLabel: { fontSize: 14, color: colors.ink700, fontWeight: "500" },
  radioSublabel: { fontSize: 12, color: colors.ink500, marginTop: 2 },

  overrideInputRow: { flexDirection: "row", alignItems: "center", marginLeft: 32, marginBottom: 6 },
  overrideInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    fontSize: 16,
    fontWeight: "600",
    color: colors.ink900,
    backgroundColor: colors.surface,
    minWidth: 90,
  },
  overrideSuffix: { fontSize: 13, color: colors.ink500, marginHorizontal: 8 },
  overrideHint: { fontSize: 12, color: colors.ink500, marginLeft: 32, marginBottom: 6, lineHeight: 16 },

  allocationLine: { fontSize: 13, color: colors.ink700, paddingVertical: 3 },
  allocationShort: { fontSize: 13, color: colors.danger, paddingVertical: 3 },
  preferToggle: { paddingVertical: 8, cursor: "pointer" },
  preferToggleText: { fontSize: 13, color: colors.primaryDark, fontWeight: "600" },
  repriceHint: { fontSize: 13, color: colors.ink500, marginTop: 10, lineHeight: 18 },

  submittingOverlay: {
    flex: 1,
    backgroundColor: colors.overlay,
    alignItems: "center",
    justifyContent: "center",
  },
  submittingText: { color: "#FFFFFF", fontSize: 15, marginTop: 12 },
});

export default EditBookingWizard;
